import Vue from "vue";
import router from "@/router";
import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";
import { isUUID } from '@/utils/uuid-utils';
import routingHistory from "@/store/services/routing-history-service";

const MAX_ROUTING_HISTORY_LENGTH = 10;

const onlyQueryStringChanges = (newHistoryObject) => {
    return isEqual(newHistoryObject.to.name, newHistoryObject.from.name) &&
        isEqual(newHistoryObject.to.params, newHistoryObject.from.params);
}

const reloadAfterPost = (newHistoryObject, lastHistoryObject) => {
    if (!isEqual(newHistoryObject.from.name, newHistoryObject.to.name)) { return false }

    const lastId = lastHistoryObject?.to?.params?.id
    const newId = newHistoryObject?.to?.params?.id;
    if (newId == undefined || lastId == undefined) { return false }

    return isUUID(lastId) && typeof !isNaN(newId)
}

const isCircularRouting = (newHistoryObject, lastHistoryObject) => {
    return isEqual(newHistoryObject.from, lastHistoryObject.to) && isEqual(newHistoryObject.to, lastHistoryObject.from);
}

const isSameRouteAfterReload = (newHistoryObject, lastHistoryObject) => {
    return isEqual(newHistoryObject.to.name, lastHistoryObject.to.name) &&
        isEqual(newHistoryObject.to.params, lastHistoryObject.to.params);
}

const isReloadObject = (newHistoryObject) => {
    return newHistoryObject.from.name == null;
}

const limitRoutingHistory = (routingHistoryStack) => {
    const diff = routingHistoryStack.length - MAX_ROUTING_HISTORY_LENGTH;

    if (diff < 0)
        return routingHistoryStack;
    
    for (let i = 0; i < diff; i++)
        routingHistoryStack.shift();
    
    return routingHistoryStack;
}

const FALLBACK_ROUTE_NAME = 'OpenWindowsView';

export default {
    namespaced: true,
    state: {
        global: [],
        ignoreNextRoutingObject: false,
        intentionalUnusualForwarding: false,
        browserBackClicked: false,
    },
    mutations: {
        changeToOfLastHistoryObject(state, newTo) {
            const indexOfLastHistoryObj = state.global.length - 1;
            Vue.set(state.global[indexOfLastHistoryObj], 'to', newTo);
        },
        addRoutingHistoryObject(state, routingHistoryObject) {
            let updatedRoutingHistoryStack = [...state.global, routingHistoryObject];
            updatedRoutingHistoryStack = limitRoutingHistory(updatedRoutingHistoryStack);
            Vue.set(state, 'global', updatedRoutingHistoryStack);
        },
        setLastRoutingHistoryObjectTo(state, routingHistoryObject) {
            const updatedRoutingHistoryStack = cloneDeep(state.global);
            updatedRoutingHistoryStack[updatedRoutingHistoryStack.length - 1].to = routingHistoryObject.to;
            Vue.set(state, 'global', updatedRoutingHistoryStack);
        },
        setLastRoutingHistoryObjectFrom(state, routingHistoryObject) {
            const updatedRoutingHistoryStack = cloneDeep(state.global);
            updatedRoutingHistoryStack[updatedRoutingHistoryStack.length - 1].from = routingHistoryObject.from;
            Vue.set(state, 'global', updatedRoutingHistoryStack);
        },
        deleteLastHistoryObject(state) {
            const updatedRoutingHistoryStack = cloneDeep(state.global);
            updatedRoutingHistoryStack.pop();
            Vue.set(state, 'global', updatedRoutingHistoryStack);
        },
        resetRoutingHistoryStack(state) {
            Vue.set(state, 'global', []);
        },
        setGlobalRoutingStack(state, globalRoutingStack) {
            Vue.set(state, 'global', globalRoutingStack);
        },
        ignoreNextRoutingObject(state) {
            Vue.set(state, 'ignoreNextRoutingObject', true);
        },
        dontIgnoreNextRoutingObject(state) {
            Vue.set(state, 'ignoreNextRoutingObject', false);
        },
        setIntentionalUnusualForwarding(state, value) {
            Vue.set(state, 'intentionalUnusualForwarding', value);
        },
        setBrowserBackClicked(state) {
            Vue.set(state, 'browserBackClicked', true);
        },
        setBrowserBackClickedToDefaultState(state) {
            Vue.set(state, 'browserBackClicked', false);
        },
        resetStore(state) {
            Vue.set(state, 'global', []);
            Vue.set(state, 'ignoreNextRoutingObject', false);
            Vue.set(state, 'intentionalUnusualForwarding', false);
            Vue.set(state, 'browserBackClicked', false);
        }
    },
    actions: {
        async handleNewRoutingObject({ commit, dispatch }, payload) {
            const newHistoryObject = routingHistory.createRoutingHistoryObject(payload);

            const lastHistoryObject = await dispatch('getLastHistoryObject');

            if (onlyQueryStringChanges(newHistoryObject)) {
                if (!lastHistoryObject) { return }
                return commit('setLastRoutingHistoryObjectTo', newHistoryObject);
            }


            if (reloadAfterPost(newHistoryObject, lastHistoryObject)) {
                return commit('changeToOfLastHistoryObject', newHistoryObject.to);
            }

            if (isReloadObject(newHistoryObject)) {
                if (lastHistoryObject == undefined) { return }

                const itIsSameRouteAfterReload = isSameRouteAfterReload(newHistoryObject, lastHistoryObject);
                if (itIsSameRouteAfterReload === true) { return }
                return commit('resetRoutingHistoryStack');
            }

            if (lastHistoryObject != undefined && isCircularRouting(newHistoryObject, lastHistoryObject)) {
                return commit('deleteLastHistoryObject');
            }

            commit('addRoutingHistoryObject', newHistoryObject);
        },
        async redirectToLastHistoryObject({ dispatch, commit }, payload) {
            const lastHistoryObject = await dispatch('getLastHistoryObject');

            const cachedTable = lastHistoryObject?.to?.query['table-cache-id'];
            if (cachedTable !== undefined) {
                commit('tableStore/deleteTableReferences', [cachedTable], { root: true });
            }

            await commit('deleteLastHistoryObject');
            await commit('ignoreNextRoutingObject');

            if (lastHistoryObject == undefined) {
                return router.push({ name: FALLBACK_ROUTE_NAME });
            }

            const savedEntityRedirectCallback = lastHistoryObject.to?.params?.savedEntityRedirectCallback;
            if (savedEntityRedirectCallback !== undefined && payload !== undefined) {
                await savedEntityRedirectCallback(payload);
            }

            router.push(lastHistoryObject.from);
        },
        setGlobalRoutingStack({ commit }, globalRoutingStack) {
            commit('setGlobalRoutingStack', globalRoutingStack);
        },
        setLastRoutingHistoryObjectFrom({ commit }, routingHistoryObject) {
            commit('setLastRoutingHistoryObjectFrom', routingHistoryObject);
        },
        getLastHistoryObject({ state }) {
            return state.global[state.global.length - 1];
        },
        deleteLastHistoryObject({ commit }) {
            commit('deleteLastHistoryObject');
        },
        resetRoutingHistoryStack({ commit }) {
            commit('resetRoutingHistoryStack');
        },
        ignoreNextRoutingObject({ commit }) {
            commit('ignoreNextRoutingObject');
        },
        dontIgnoreNextRoutingObject({ commit }) {
            commit('dontIgnoreNextRoutingObject');
        },
        setIntentionalUnusualForwarding({ commit }, value) {
            commit('setIntentionalUnusualForwarding', value);
        },
        setBrowserBackClicked({ commit }) {
            commit('setBrowserBackClicked');
        },
        setBrowserBackToDefaultState({ commit }) {
            commit('setBrowserBackClickedToDefaultState');
        },
        resetStore({ commit }) {
            commit('resetStore');
        }
    },
    getters: {
        getCurrentRoutingHistory: state => state.global,
        getIgnoreNextRoutingObject: state => state.ignoreNextRoutingObject,
        getIntentionalUnusualForwarding: state => state.intentionalUnusualForwarding,
        getBrowserBackClicked: state => state.browserBackClicked,
    }
}
